/**
 * Thin HTTP layer for API communication
 *
 * This package provides only the base request infrastructure.
 * Feature-specific API methods should be defined in their respective packages.
 */

import { deriveApiOrigin } from './origin.js'

// API base resolution (#234 clean-A, no env drift):
//   1. VITE_API_URL — explicit build-time override / rollback lever only.
//   2. Runtime derivation from the frontend hostname (origin.ts) — the normal
//      mode for every deployed env, including per-PR pages.dev previews.
//   3. '' — same-origin (localhost dev via the vite proxy, unknown hosts).
const REAL_API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (typeof window !== 'undefined' ? deriveApiOrigin(window.location.hostname) : '')
let API_BASE = REAL_API_BASE

export { deriveApiOrigin, deriveWsOrigin, derivePreviewOrigin } from './origin.js'

/**
 * Active tenant ID getter.
 * Set by the frontend TenantProvider so all API requests include X-Tenant-Id.
 */
let activeTenantIdGetter: (() => string | null) | null = null

/**
 * Active org email getter.
 * For multi-email same-org, identifies which email identity to use.
 */
let activeOrgEmailGetter: (() => string | null) | null = null

export function setActiveTenantGetter(getter: () => string | null): void {
  activeTenantIdGetter = getter
}

export function setActiveOrgEmailGetter(getter: () => string | null): void {
  activeOrgEmailGetter = getter
}

/**
 * A scoped identity that wins over the getters above — for the api-client's own
 * requests to paths `appliesTo` accepts — until the returned `restore()` runs.
 * For a surface that must act as an identity the URL doesn't carry: the share
 * viewer's in-place PDApp Settings (#757) lives on `/share-view/…`, where
 * TenantProvider always resolves the viewer's PERSONAL tenant, yet an org-owned
 * app's /api/pdapp routes must be called as the org membership the viewer
 * picked. Request HEADERS are PATH-SCOPED on purpose (`appliesTo`): a page-wide
 * header override leaked into unrelated pollers (write-status cached the org's
 * answer under the personal tenant). The identity READS `getActiveTenantId()` /
 * `getActiveOrgEmail()` do follow the override while it is installed — the
 * overriding surface reads them for the same identity (e.g. the settings folder
 * picker's drive root); install one only around a surface that owns the page.
 * Nothing is granted: the server re-resolves membership + rights for whatever
 * it is sent. Nests (restore reinstates the previous override).
 */
type IdentityOverride = { tenantId: string | null; orgEmail: string | null; appliesTo: (path: string) => boolean }
let identityOverride: IdentityOverride | null = null
export function overrideActiveIdentity(
  identity: { tenantId: string | null; orgEmail: string | null },
  appliesTo: (path: string) => boolean,
): () => void {
  const previous = identityOverride
  identityOverride = { ...identity, appliesTo }
  return () => { identityOverride = previous }
}
const overrideFor = (path?: string): IdentityOverride | null =>
  identityOverride && path !== undefined && identityOverride.appliesTo(path) ? identityOverride : null
const currentTenantId = (path?: string): string | null => {
  const o = overrideFor(path)
  return o ? o.tenantId : (activeTenantIdGetter?.() ?? null)
}
const currentOrgEmail = (path?: string): string | null => {
  const o = overrideFor(path)
  return o ? o.orgEmail : (activeOrgEmailGetter?.() ?? null)
}

/**
 * Share-session token getter for share-view pages. When set, every API
 * request includes an `X-Share-Session` header so server-side routes
 * that opt into the share principal (currently /api/sqlite/*) can
 * authorize the recipient against the share row instead of Drive auth.
 * Top-level page route (/share-view/<sid>) sets this on mount and clears
 * on unmount — no need for per-request plumbing.
 */
let shareSessionTokenGetter: (() => string | null) | null = null

export function setShareSessionTokenGetter(getter: () => string | null): void {
  shareSessionTokenGetter = getter
}

/**
 * A refused request, reported to anyone who asked to hear about them.
 *
 * ── Why this layer, and why it carries no meaning ───────────────────────
 *
 * Some refusals are really statements about the TENANT rather than about the
 * request: uploads are paused, or the tenant is identity-only. The surface that
 * explains those to the user (`WriteStatusProvider` in `@papan/core-ui`) learns
 * them from a polled endpoint, so the refusal and the sentence explaining it can
 * arrive up to a poll interval apart — the user sees a bare failure and no
 * reason. The fix is for the refusal itself to prompt a re-read, and this is the
 * one place every `request()` failure passes through, so it is the only place
 * that can do it without touching every call site.
 *
 * This module stays DUMB on purpose: it reports `{ status, code, path }` and
 * knows nothing about which codes mean what. Teaching a thin HTTP layer about
 * tenant modes would put the meaning in the wrong package (`@papan/api-client`
 * has no business importing product state) and would silently limit the
 * mechanism to whatever codes were listed here. The consumer filters.
 *
 * Listeners must not throw and must not issue a request that can itself fail
 * with the code they are listening for — that is a loop with no brake here.
 *
 * ── Coverage, stated ────────────────────────────────────────────────────
 *
 * `request()` only. `fetchRaw` cannot join: it hands the caller an unread
 * `Response`, and the `code` lives in a JSON body that reading here would
 * consume out from under them. That is a real gap for content/download reads,
 * and an acceptable one — the listing endpoints a refusal shows up on first
 * (`/api/files`, `/api/bin`) all go through `request()`. Uploads are a third
 * pipeline (tus) and already carry their own handler. If a future consumer
 * needs the raw path too, the answer is a coded response HEADER, not a body
 * read here.
 */
export interface ApiFailure {
  /** HTTP status of the refused response. */
  status: number
  /** The server's `code` field when it sent one. Branch on this, not `message`. */
  code?: string
  /** Request path as passed to `request()`, so a listener can ignore its own. */
  path: string
  /**
   * On a 429, the server's `retryAfterSec` from the JSON body (it also sends a
   * `Retry-After` header, but that isn't CORS-exposed, so the body is the
   * portable source). Present only when rate-limited (#341 Case D).
   */
  retryAfterSec?: number
  /** E7 PR 0b (D134) — the request acted as a PINNED identity that is no longer the current one (an undo started
   *  before a switch): its refusal speaks about THAT identity, never the current session (a listener such as the
   *  tenant-denied screen ignores it). */
  staleIdentity?: boolean
  /**
   * The server marked this refusal as explaining itself (`selfExplained: true`, #1217's proof-code 429s): the thrown
   * error keeps the server's sentence, and a generic listener (the app's rate-limit toast) should stay quiet — the
   * page that asked shows it.
   */
  selfExplained?: boolean
  /**
   * The refusal's parsed JSON body, when there was one.
   *
   * `code` alone is enough to DECIDE what a listener does; this is for
   * listeners that must also SAY something with the server's figures — the
   * chat-storage refusals carry `usedBytes` / `limitBytes` / `quotaBytes`,
   * and a dialog that quoted the server's English `error` string instead
   * could not be translated. Deliberately untyped: this is a generic
   * transport, and naming one feature's fields here would invite the next
   * one to add its own.
   */
  body?: Record<string, unknown>
}

/**
 * A Set rather than a single slot, unlike the getters above: those are
 * write-once wiring, this is a subscription with a lifetime. React StrictMode
 * mounts an effect twice, so a single-slot version would leave the second
 * mount's unsubscribe clearing a listener the first mount still needs.
 */
const apiFailureListeners = new Set<(failure: ApiFailure) => void>()

/** Subscribe to refused requests. Returns the unsubscribe. */
export function onApiFailure(listener: (failure: ApiFailure) => void): () => void {
  apiFailureListeners.add(listener)
  return () => { apiFailureListeners.delete(listener) }
}

/**
 * Publish a refused request to the `onApiFailure` listeners.
 *
 * `request()` below calls this for you. It is EXPORTED for the one transport
 * that cannot use `request()`: the AG-UI run POSTs ordinary JSON but reads a
 * server-sent-event RESPONSE, which `request()` has no way to return, so it
 * calls `fetch` directly and owns its own abort and retry
 * (`features/agent/ui/hooks.ts`).
 *
 * (Spelled out rather than written as the MIME type: the #850 guard
 * `no-unbounded-stream-writes.test.ts` greps the tree for that string to
 * find SSE ROUTES, and a mention of it in this client file read as one.) That made it invisible
 * here — and since AG-UI is the agent's primary run transport, every
 * shell-level listener silently did not fire for the main chat send. Found by
 * the scoped review of round 1, finding 4, after a comment had already
 * claimed the opposite.
 *
 * A direct caller owns what `request()` would have filled in: pass the real
 * request `path` (listeners filter on it) and the parsed JSON body.
 */
export function reportApiFailure(failure: ApiFailure): void {
  for (const listener of apiFailureListeners) {
    // One bad listener must not turn a 403 into an unhandled exception on the
    // throwing path — the caller's own error handling is what matters here.
    try { listener(failure) } catch { /* ignore */ }
  }
}

/**
 * Read the current active tenant + org-email identity. Used by the React-Query
 * key factories so cache entries don't bleed across identities — switching org
 * email mid-session must not serve a list cached under the previous identity.
 *
 * Returns `null` if the getter hasn't been wired yet (pre-mount / SSR).
 */
export function getActiveTenantId(): string | null {
  return identityOverride ? identityOverride.tenantId : currentTenantId()
}

export function getActiveOrgEmail(): string | null {
  return identityOverride ? identityOverride.orgEmail : currentOrgEmail()
}

/**
 * Read the active share-session token, if a share-view page has wired one.
 * `null` in the main app. Used by the session-loss detector to exclude
 * share-view 401s (which are expected, not a session death).
 */
export function getShareSessionToken(): string | null {
  return shareSessionTokenGetter?.() ?? null
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  body?: BodyInit
  headers?: Record<string, string>
  /**
   * Per-call abort signal. When omitted, request() applies a default timeout
   * (DEFAULT_REQUEST_TIMEOUT_MS) so a stalled response can never hang the caller
   * forever. Pass an explicit signal to opt out, or to use a tighter bound +
   * retry (see the workspace client's fetchWorkspaceJson).
   */
  signal?: AbortSignal
  /** E7 PR 0b — act as THIS identity instead of the current one (an undo pins the session that started it, so a
   *  tenant / identity switch mid-undo can't send its follow-on requests as the new context). See
   *  `captureRequestIdentity`. Replaces the tenant / org-email / share-session headers; `headers` still win. */
  identity?: RequestIdentity
}

/**
 * Default per-request timeout. The JSON api layer should always get a quick
 * response; a request still pending after this long has STALLED (latency,
 * packet-loss, a hung upstream / pool exhaustion) — not "slow but coming". With
 * no timeout a stalled fetch never settles and wedges whatever UI awaits it
 * ("Loading workspace..." was one such forever-spinner). Generous enough that
 * only a genuine stall trips it, never a legitimately-slow-but-completing read.
 * Uploads (tus) and blob/stream reads (fetchRaw) are on separate paths and are
 * intentionally NOT bounded here.
 */
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000

/**
 * Tighter timeout for idempotent GETs, paired with ONE retry (see
 * getWithStallRetry). A GET still pending after this has STALLED on the
 * Cloudflare edge: the origin answered in <1s but the response was lost or
 * badly delayed on the CF→client leg (packet-level proof + per-endpoint
 * latency measurements in docs/2026-06-13_CF_RESPONSE_STALL.md / #155). The
 * old behavior was a single 30s hang then a thrown error — a forever-spinner
 * for the user. Re-issuing the GET gets a fresh connection and the origin
 * responds immediately.
 *
 * 8s is >5× the measured GET p99 (every JSON read is <1.5s even at 8×
 * concurrency), so a legitimately-slow-but-completing read won't trip it;
 * only a stall does. GET-only: a stalled mutation may have committed
 * server-side, so POSTs keep the 30s/no-retry path (retrying could
 * double-submit). Callers passing their own signal opt out of both the
 * tight timeout and the retry, same as the default-timeout opt-out.
 */
const GET_STALL_TIMEOUT_MS = 8_000

/**
 * Get the API base URL
 */
export function getApiBase(): string {
  return API_BASE
}

/**
 * Dev only: switch API base to an unreachable URL to simulate offline,
 * or back to the real URL to go online.
 */
export function setDevOfflineApi(offline: boolean): void {
  API_BASE = offline ? 'http://localhost:1' : REAL_API_BASE
}

/**
 * Get default headers for API requests.
 * Browser sessions use cookies (handled automatically by fetch with credentials).
 * Includes X-Tenant-Id when an active tenant is set.
 */
/** E7 PR 0b — the identity a request acts as (the tenant / org-email / share-session headers), captured once.
 *  `account`: which signed-in account it was captured under (the app registers the counter — `setAccountGetter`).
 *  `actingOrgEmail` (D134): when the tab had NO org email of its own, the one the server last said it ACTED as for that
 *  tenant (`X-Acting-Org-Email`) — so a pinned identity can name it explicitly instead of letting the server use its
 *  stored choice, which another tab may change. */
export interface RequestIdentity {
  tenantId: string | null; orgEmail: string | null; shareSession: string | null; account?: number
  actingOrgEmail?: string
  /** …and the tenant's echo VERSION then (`actingEchoVersion`): any change since means it can't be trusted. */
  actingVersion?: number
}

/** D134 — per tenant, what the server says it acts as for a request that sends NO org email (its stored choice):
 *  the email, and a version bumped whenever such an answer names a DIFFERENT email than before (another tab switched). */
const actingOrgEmails = new Map<string, { email: string; version: number; account?: number }>()
/** The org email the server last acted as for a no-email request to `tenantId` in this tab, if any said so — for the
 *  account signed in NOW (#1316: an echo from before a sign-out / another user names someone else's identity). */
export function lastActingOrgEmail(tenantId: string | null): string | undefined {
  const entry = tenantId ? actingOrgEmails.get(tenantId) : undefined
  if (!entry || (entry.account !== undefined && entry.account !== accountGetter?.())) return undefined
  return entry.email
}
/** How many times that answer has CHANGED for `tenantId` (0 = never seen) — an op pins its email only if unchanged. */
export function actingEchoVersion(tenantId: string | null): number {
  return tenantId ? actingOrgEmails.get(tenantId)?.version ?? 0 : 0
}
/** Only an answer to a request that sent NO `X-Org-Email` says what a no-email request gets (one that named its email
 *  — a pinned undo step, a request under `overrideActiveIdentity` — says nothing about the stored choice). */
export function recordActingIdentity(res: Response, sentHeaders: Record<string, string>, sentAccount = accountGetter?.()): void {
  noteActingIdentity(res, sentHeaders, sentAccount)
}
/** The account a request is sent under — read it just before a raw `fetch` and pass it to `recordActingIdentity`, so a
 *  late answer is never recorded for the account signed in when it lands (#1316). */
export function currentAccount(): number | undefined {
  return accountGetter?.()
}
/** #1210 — the org email THIS answer was read as, per parsed body. */
const actingOfBody = new WeakMap<object, string>()
function stampActingIdentity(body: unknown, res: Response, sentHeaders: Record<string, string>): void {
  if (body === null || typeof body !== 'object') return
  // the email the request named, else the one the server says it acted as for a request that named none
  const email = sentHeaders['x-org-email'] || res.headers?.get?.('x-acting-org-email')
  if (email) actingOfBody.set(body, email)
}
/** Carry `from`'s stamp to `to` — for a client method that returns a PART of the parsed body (#1210). */
export function carryActingStamp(from: unknown, to: unknown): void {
  const email = actingOrgEmailOf(from)
  if (email && to !== null && typeof to === 'object') actingOfBody.set(to, email)
}
/** The org email the request that produced `body` was read as — the email it sent, else the server's echo for one
 *  that sent none; `undefined` when neither is known (a copy, a patch, an older server). Unlike `lastActingOrgEmail`
 *  it belongs to this one answer: another response can't overwrite it (#1210). */
export function actingOrgEmailOf(body: unknown): string | undefined {
  return body !== null && typeof body === 'object' ? actingOfBody.get(body) : undefined
}
function noteActingIdentity(res: Response, sentHeaders: Record<string, string>, sentAccount = accountGetter?.()): void {
  if (sentHeaders['x-org-email']) return
  // #1316: an answer sent under one account and landing after the next signed in is that account's — never recorded
  // as the current one's (its identity would be pinned on the next account's work)
  if (sentAccount !== accountGetter?.()) return
  const tenant = res.headers?.get?.('x-acting-tenant-id')
  const email = res.headers?.get?.('x-acting-org-email')
  if (!tenant || !email) return
  const prev = actingOrgEmails.get(tenant)
  const account = accountGetter?.()
  // another account's entry is no history of this one's: its first echo starts again at version 1 (#1316, D134)
  if (!prev || prev.account !== account) actingOrgEmails.set(tenant, { email, version: 1, account })
  else if (prev.email !== email) actingOrgEmails.set(tenant, { email, version: prev.version + 1, account })
}

let accountGetter: (() => number) | null = null
/** The app wires which signed-in account is current (core-ui's session-ended `accountEpoch`). */
export function setAccountGetter(getter: () => number): void {
  accountGetter = getter
}

// ── #1316: an answer that lands after its session ended is DROPPED ──────────────────────────────────────────────────
// Sign-out, another account, a confirmed tenant / identity switch and leaving the share view end the session (core-ui's
// `endSession`, the one primitive). A request still in flight then must not show the OLD session's data to the new one
// — "Renamed Payroll September.xlsx", a patch to the old tenant's cached list. So the session's signal is read when the
// request STARTS, and a later answer — success or failure — becomes a `SessionEndedError` (an `AbortError`): no success
// path runs (no toast, no cache patch), every `finally` does. The request itself is NOT aborted — a write the server
// already has keeps running there (a restore, an undo, a rename); only what it would SHOW is dropped.
//
// Why a rejection and not a promise that never settles: the SPA keeps running across a session end, and app-lifetime
// state is released in `finally` (the undo lock, in-flight delete / restore markers, draft-save counters, react-query's
// loading states, module promise caches) — a never-settling answer would leave each stuck until a reload (P13: nothing
// held that nothing will release; a stuck lock is a false state, tier 2 — decision D1 in the #1316 PR). And so that
// a caller's `catch` that toasts ("Couldn't rename Payroll September.xlsx") shows nothing either: while the rejection's
// continuations run — the same task, before any timer — `isDroppingLateAnswer()` is true and the toast provider refuses
// new toasts (core-ui Toast.tsx).

/** The error a dropped answer becomes: an `AbortError` (abort-aware code already ignores it) marked `sessionEnded`. */
export class SessionEndedError extends Error {
  readonly sessionEnded = true
  constructor() {
    super('The session ended before this answer arrived.')
    this.name = 'AbortError'
  }
}
/** Is `e` a dropped late answer (#1316)? Every error surface ignores it: nothing failed for the person now here. */
export function isSessionEndedError(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as { sessionEnded?: unknown }).sessionEnded === true
}

/** #1316 review round 1 (owner, final) — THE text an API error becomes on screen (an error line, a load-failed state):
 *  `null` for a dropped late answer — nothing failed for the person now here, so the caller shows nothing and keeps
 *  what it shows — else the error's message, else `fallback` (a translated text the caller passes), else the value
 *  itself. `no-dropped-answer-as-error-text-1316.test.ts` pins every caught `.message` that reaches a state setter. */
export function errorText(err: unknown, fallback?: string): string | null {
  if (isSessionEndedError(err)) return null
  const message = err && typeof err === 'object' ? (err as { message?: unknown }).message : undefined
  if (typeof message === 'string' && message) return message
  if (fallback !== undefined) return fallback
  if (typeof err === 'string' && err) return err
  return 'Unknown error'
}

let droppingLateAnswer = false
/** True while a dropped answer's continuations run (the rest of this task): the toast provider shows nothing then. */
export function isDroppingLateAnswer(): boolean {
  return droppingLateAnswer
}
function droppedAnswer(): Promise<never> {
  if (!droppingLateAnswer) {
    droppingLateAnswer = true
    // a macrotask: every microtask continuation of this rejection (the caller's catch → toast) runs before it.
    // Accepted residuals (a toast doesn't say which session raised it, so no window is exact): another task that runs
    // before this timer — a burst of old answers at a switch, rarely a new-session answer in the same few ms — is
    // quiet too; a catch that waits on a TIMER or a request before toasting outlives the window (none known).
    setTimeout(() => { droppingLateAnswer = false }, 0)
  }
  return Promise.reject(new SessionEndedError())
}

let sessionSignalGetter: (() => AbortSignal) | null = null
/** The app wires the current session's signal (core-ui's session-ended `sessionSignal`). Unset (a test, a worker
 *  bundle) = no session: nothing is dropped. */
export function setSessionSignalGetter(getter: () => AbortSignal): void {
  sessionSignalGetter = getter
}

/** The current session's signal, if the app wired one — read it when work STARTS. */
export function currentSessionSignal(): AbortSignal | undefined {
  return sessionSignalGetter?.()
}

/** The one check for the global retry rule (#1316): a failure that is a dropped answer, or any failure after the session
 *  that started the work (`start`) ended, is never retried or reported — whoever retried would send as the NEXT session. */
export function sessionEndedSince(start: AbortSignal | undefined, err?: unknown): boolean {
  return isSessionEndedError(err) || !!start?.aborted
}

/** A signal for a STREAM's body read: aborts when the caller's `signal` OR the CURRENT session's signal does (the read
 *  ends when the session ends). Either may be absent; both absent = `undefined`. */
export function sessionStreamSignal(signal?: AbortSignal): AbortSignal | undefined {
  const session = currentSessionSignal()
  const parts = [signal, session].filter((s): s is AbortSignal => !!s)
  if (parts.length === 0) return undefined
  if (parts.length === 1) return parts[0]
  if (typeof AbortSignal.any === 'function') return AbortSignal.any(parts)
  const ctrl = new AbortController()
  // No `AbortSignal.any`: the first abort removes the listener from EVERY part, so nothing piles up on the long-lived
  // session signal (a stream per call would otherwise leave one dead listener each).
  const offs: Array<() => void> = []
  const abortFrom = (s: AbortSignal) => {
    for (const off of offs) off()
    ctrl.abort(s.reason)
  }
  for (const s of parts) {
    if (s.aborted) { abortFrom(s); break }
    const fn = () => abortFrom(s)
    s.addEventListener('abort', fn, { once: true })
    offs.push(() => s.removeEventListener('abort', fn))
  }
  return ctrl.signal
}

/**
 * Settles as `work` does — unless `session` (the one current when the work started; default: now) has ENDED by the time
 * it settles: then it rejects with a `SessionEndedError` (success or failure alike), with toasts held off while that
 * rejection is handled. The work itself goes on.
 */
export function dropIfSessionEnded<T>(work: Promise<T>, session: AbortSignal | undefined = currentSessionSignal()): Promise<T> {
  if (!session) return work
  return work.then(
    (v) => {
      if (!session.aborted) return v
      // a dropped raw Response's body is released, not left holding its connection until GC
      if (typeof Response !== 'undefined' && v instanceof Response) void v.body?.cancel().catch(() => {})
      return droppedAnswer()
    },
    (e) => (session.aborted ? droppedAnswer() : Promise.reject(e)),
  )
}

/** #1316 codex review — the body readers of a session-bound answer, each re-checking the session that SENT it: a body
 *  still downloading when that session ended is dropped like a late answer (a `SessionEndedError` through
 *  `droppedAnswer`, toasts held off), success or not. Applied by `sessionFetch` and the bound `fetchRaw`, so a caller
 *  that reads `res.json()` directly needs nothing of its own. A pinned-identity answer is never bound (it must finish);
 *  a stream that reads `res.body` itself uses `sessionStreamSignal`. Works on a test's plain-object fake as on a real
 *  Response (own properties shadow the prototype's readers); one that can't take them is left as it is. */
const BODY_READERS = ['json', 'text', 'blob', 'arrayBuffer', 'bytes', 'formData'] as const

function bindBodyToSession<T>(res: T, session: AbortSignal | undefined): T {
  if (!session || !res || typeof res !== 'object') return res
  const target = res as Record<string, unknown>
  try {
    for (const reader of BODY_READERS) {
      const read = target[reader]
      if (typeof read !== 'function') continue
      Object.defineProperty(target, reader, {
        configurable: true, writable: true,
        // the native reader starts NOW (`bodyUsed` / a following `clone()` behave as native); only its settled value is judged
        value: (...args: unknown[]) => dropIfSessionEnded(Promise.resolve(read.apply(res, args)), session),
      })
    }
    const clone = target.clone
    if (typeof clone === 'function') {
      Object.defineProperty(target, 'clone', { configurable: true, writable: true, value: () => bindBodyToSession(clone.call(res), session) })
    }
  } catch { /* a frozen fake: left as it is */ }
  return res
}

/** #1316 review round 1: a session-bound answer's JSON BODY, read in the session that sent it (`start`, captured when the
 *  request started). The Response of `sessionFetch` / the bound `fetchRaw` already re-checks its own session on every
 *  body read (`bindBodyToSession`); this keeps the same check for a Response from anywhere else.
 *  `onUnreadable`: the value for a body that isn't JSON (else that failure rejects). */
export function readInSession<T = unknown>(res: Response, start: AbortSignal | undefined, onUnreadable?: () => T): Promise<T> {
  const read = res.json() as Promise<T>
  return dropIfSessionEnded(onUnreadable ? read.catch(onUnreadable) : read, start)
}

/** `fetch`, bound to the session current when it starts (`dropIfSessionEnded`) — the share view's raw fetches. Its
 *  answer feeds the identity echo like every raw fetch (a no-op when the request named its org email, or the answer
 *  names no acting identity — a share session's). */
export function sessionFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const sent: Record<string, string> = {}   // lower-cased, as noteActingIdentity reads
  new Headers(init?.headers).forEach((value, name) => { sent[name] = value })
  const session = currentSessionSignal()
  return dropIfSessionEnded(fetch(input, init), session).then((res) => { recordActingIdentity(res, sent); return bindBodyToSession(res, session) })
}

/** The identity requests act as right now — capture it when work that must finish in THIS session starts. */
export function captureRequestIdentity(): RequestIdentity {
  const tenantId = currentTenantId()
  const orgEmail = currentOrgEmail()
  const acting = orgEmail ? undefined : lastActingOrgEmail(tenantId)
  return {
    tenantId, orgEmail, shareSession: shareSessionTokenGetter?.() ?? null,
    ...(accountGetter ? { account: accountGetter() } : {}),
    ...(acting ? { actingOrgEmail: acting, actingVersion: actingEchoVersion(tenantId) } : {}),
  }
}

/** Is `identity` still the one requests act as now? Judged by what it is SENT as (`identityHeaders`): an identity that
 *  names an org email — its own, or the acting echo it pinned (D134) — is current only while the tab's requests go out
 *  under that same email (#1316 review round 2: an echo-pinned identity is NOT the tab's after a same-tenant switch to
 *  another email). One naming NONE ("the server picks") matches whatever the tab resolved since — the same account, the
 *  same identity. Another tenant / share session / account is always a different identity. */
export function isCurrentIdentity(identity: RequestIdentity): boolean {
  const now = captureRequestIdentity()
  if (now.tenantId !== identity.tenantId || now.shareSession !== identity.shareSession) return false
  if (now.account !== identity.account) return false
  const sent = identity.orgEmail ?? identity.actingOrgEmail ?? null
  return sent === null || (now.orgEmail ?? now.actingOrgEmail ?? null) === sent
}

/** #1316 review round 2 (owner, 2026-10-28) — a multi-step WRITE a person started: a move / paste of several items, a
 *  page Save of several rows, a bulk delete. It FINISHES as the identity that started it — like an undo (D134) — never
 *  stopped half-way by a same-person session end (a tenant / identity switch), and never sent on as the NEXT session
 *  (the old tenant's ids under the new tenant's headers, a write audited as the other email). Take it at the start;
 *  every step spreads `batch.opts()` (`{ identity }`: sent as the starter, exempt from the drop); what the batch SHOWS
 *  — toasts, errors, progress, a dialog, an undo record — is gated on `!batch.ended()`. An account end reloads the page
 *  (it never gets here); an identity that can't be named once its session ended refuses its next step instead
 *  (`isPinnedBatchIdentityLost`). */
export interface PinnedBatch {
  /** The identity every step is sent as. */
  readonly identity: RequestIdentity
  /** The session current when the batch started. */
  readonly start: AbortSignal | undefined
  /** The request options for a step: `{ identity }`. An identity that names no org email (the tab had none and no answer
   *  said which the server acts as) can't be pinned: `{}` while its session lasts (the step goes as the tab does, bound
   *  to the session), and once it ended a `PinnedBatchIdentityLostError` — the server would use ITS stored choice, the
   *  new one. Another account since the start throws too. */
  opts(): { identity?: RequestIdentity }
  /** One step of the batch through a client method that takes no identity option: `batch.run(() => api.save(x))`. The
   *  request(s) the step STARTS SYNCHRONOUSLY (`request()` / `fetchRaw` called before the method's first `await`) are
   *  sent as the batch's identity, exactly as with `opts()`. A method that awaits before its request isn't pinned this
   *  way (logged) — give it an identity option and pass `opts()`. Throws as `opts()` does. */
  run<T>(step: () => Promise<T>): Promise<T>
  /** Has the session that started the batch ended? Then nothing about the batch is shown. */
  ended(): boolean
}

/** `PinnedBatch.run`'s identity, set only for the synchronous span of a step (JS runs nothing else meanwhile, so only
 *  that step's own requests read it). */
let stepPin: RequestIdentity | null = null
let stepPinReads = 0
function takeStepPin(): RequestIdentity | null {
  if (stepPin) stepPinReads++
  return stepPin
}

export class PinnedBatchIdentityLostError extends Error {
  readonly code = 'BATCH_IDENTITY_LOST'
  constructor() {
    super('The session that started this ended; its identity can no longer be named')
    this.name = 'PinnedBatchIdentityLostError'
  }
}
export function isPinnedBatchIdentityLost(err: unknown): err is PinnedBatchIdentityLostError {
  return (err as { code?: unknown } | null)?.code === 'BATCH_IDENTITY_LOST'
}

export function pinnedBatch(): PinnedBatch {
  const start = currentSessionSignal()
  const identity = captureRequestIdentity()
  const ended = () => sessionEndedSince(start)
  return {
    identity,
    start,
    ended,
    opts,
    run<T>(step: () => Promise<T>): Promise<T> {
      const pin = opts().identity
      if (!pin) return step()
      const prev = stepPin
      const reads = stepPinReads
      stepPin = pin
      try {
        return step()
      } finally {
        stepPin = prev
        if (stepPinReads === reads) console.error('[pinnedBatch] a step sent no request synchronously — it was NOT pinned; pass batch.opts() to it instead')
      }
    },
  }
  function opts(): { identity?: RequestIdentity } {
    const named = !!(identity.orgEmail || identity.actingOrgEmail || identity.shareSession)
    if (ended() && (!named || identity.account !== accountGetter?.())) throw new PinnedBatchIdentityLostError()
    return named ? { identity } : {}
  }
}

/** A step that may be part of a pinned batch: `batch.run(step)` when there is one, else the step as it is. For a helper
 *  that takes an optional `batch` (a section save, an injected saver) and sends several requests. */
export function inBatch<T>(batch: PinnedBatch | undefined, step: () => Promise<T>): Promise<T> {
  return batch ? batch.run(step) : step()
}

/** The headers a request carries for `identity` (what `getDefaultHeaders` gives for the current one). */
export function identityHeaders(identity: RequestIdentity): Record<string, string> {
  const headers: Record<string, string> = {}
  if (identity.tenantId) headers['x-tenant-id'] = identity.tenantId
  // the explicit choice, else the email the server said it acted as (never the server's stored choice of the moment)
  const email = identity.orgEmail ?? identity.actingOrgEmail
  if (email) headers['x-org-email'] = email
  if (identity.shareSession) headers['x-share-session'] = identity.shareSession
  return headers
}

export function getDefaultHeaders(options?: { path?: string }): Record<string, string> {
  // #1316 review round 2: inside a pinned batch's step (`batch.run`) the headers are the batch's
  const pin = takeStepPin()
  if (pin) return identityHeaders(pin)
  const headers: Record<string, string> = {}
  const tenantId = currentTenantId(options?.path)
  if (tenantId) {
    headers['x-tenant-id'] = tenantId
  }
  const orgEmail = currentOrgEmail(options?.path)
  if (orgEmail) {
    headers['x-org-email'] = orgEmail
  }
  const shareToken = shareSessionTokenGetter?.()
  if (shareToken) {
    headers['x-share-session'] = shareToken
  }
  return headers
}

/**
 * Make an API request with automatic error handling.
 *
 * GETs are de-duplicated while in flight: if the same path + tenant + org-email
 * is requested concurrently, all callers share the single underlying fetch.
 * This is a safety net against React effect fan-out (multiple hooks demanding
 * the same resource) and disappears once the network promise settles.
 *
 * Callers must treat returned objects as read-only — they may be shared between
 * callers in the dedup window. Mutating in place will leak across consumers.
 */
const inflightGets = new Map<string, Promise<unknown>>()

/**
 * END THE IN-FLIGHT GET WINDOW — the same rule a mutation applies (see `request()`), for a REALTIME RESYNC (#1210):
 * events were missed while the socket was down, so a GET sent before the reconnect may answer with rows from before
 * the outage. Every GET asked after this starts a flight of its own; GETs already running still settle for their own
 * callers. Called by the realtime client when a `resync` arrives, before any resync handler reloads anything.
 */
export function endInFlightGetWindow(): void {
  inflightGets.clear()
}

const resyncBoundaryListeners = new Set<() => void>()

/**
 * A REALTIME RESYNC BOUNDARY (#1210): events were missed while the socket was down, so anything asked before this
 * may predate them. Ends the in-flight GET window (`endInFlightGetWindow`), then tells every listener — the file
 * caches count it as an access change (`noteAccessChanged`, registered by `FileListCacheSync`). Called ONCE per
 * resync by the realtime client, before any resync handler runs, so no handler's read can start before it. A
 * listener that throws is logged; the others and the resync handlers still run.
 */
export function markResyncBoundary(): void {
  endInFlightGetWindow()
  for (const listener of [...resyncBoundaryListeners]) {
    try { listener() } catch (error) { console.error('[api-client] resync boundary listener failed', error) }
  }
}

/** Listen for `markResyncBoundary`; returns the unsubscribe. */
export function onResyncBoundary(listener: () => void): () => void {
  resyncBoundaryListeners.add(listener)
  return () => { resyncBoundaryListeners.delete(listener) }
}

function dedupKey(path: string, headers: Record<string, string>): string {
  // Include tenant + org-email so a GET for the same path under a different
  // identity doesn't mistakenly share the response.
  // (+ the share session: a share view and the drive can resolve to the same tenant — #1316)
  return `${path}\0${headers['x-tenant-id'] || ''}\0${headers['x-org-email'] || ''}\0${headers['x-share-session'] || ''}`
}

/** #1316: what a request was SENT as besides its headers — the session and the account, read with the headers (once,
 *  so a stall retry reuses them as it reuses the headers). */
interface SentAs { session: AbortSignal | undefined; account: number | undefined }
function sentAsNow(): SentAs {
  return { session: currentSessionSignal(), account: accountGetter?.() }
}

async function doRequest<T>(
  path: string,
  options: RequestOptions,
  headers: Record<string, string>,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
  sentAs: SentAs = sentAsNow(),
): Promise<T> {
  // Default timeout via a CLEARED AbortController, not AbortSignal.timeout: the
  // timer must be disposed the moment the request settles. An uncleared 30s
  // timer leaks across the process — and in a happy-dom test env it outlives
  // the environment and fires after teardown ("ReferenceError: window is not
  // defined"). Callers that pass their own signal opt out of the default.
  let timer: ReturnType<typeof setTimeout> | undefined
  let signal = options.signal
  if (!signal) {
    const controller = new AbortController()
    timer = setTimeout(
      () => controller.abort(new DOMException('Request timed out', 'TimeoutError')),
      timeoutMs,
    )
    signal = controller.signal
  }

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: options.method || 'GET',
      headers,
      body: options.body,
      credentials: 'include',
      signal,
    })
    noteActingIdentity(res, headers, sentAs.account)   // D134: which identity the server acted as (for a no-email request)

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: res.statusText }))
      // ONE answer for both uses — keeping the server's sentence and telling listeners (#1217): a 429 the server marked
      // `selfExplained` that also carries the sentence. Anything else gets the client's generic wording and the toast.
      const selfExplained = res.status === 429 && (error as { selfExplained?: unknown }).selfExplained === true
        && typeof (error as { error?: unknown }).error === 'string'

      // Attach the HTTP status so callers can do status-aware handling
      // (e.g. bounded retry of a transient 404) without parsing the message.
      // Message text is unchanged — existing catch-by-message consumers and
      // the 503 copy are untouched; this only *adds* an `.status` field.
      const status = res.status
      const fail = (msg: string): never => {
        const e = new Error(msg) as Error & {
          status?: number
          code?: string
          email?: string
          issues?: Array<{ path: string; message: string }>
          availableEmails?: string[]
          availableIdentities?: Array<{ email: string; role: string }>
          tenantName?: string | null
          retryAfterSec?: number
          existingName?: string
          wouldSaveAs?: string
          detail?: string
          entityRoot?: boolean
          needsManage?: boolean
          wouldLoseAccess?: boolean
          restorePlan?: { recreate: Array<{ name: string; fromBinCopy: boolean }>; into: string | null; fingerprint: string }
          canRelocate?: boolean
          folderName?: string
          reservedName?: string
          canRestoreNewer?: boolean
          failedFolderId?: string
          unknownColumns?: string[]
          partial?: { fileIds: string[]; topFileIds?: string[]; versioned: Array<{ fileId: string; previousVersionId: string; versionId?: string }>; destinationFolderId: string | null }
          versionUnsavedReason?: string
          candidates?: Array<{ tenantId: string; name: string; type: string }>
        }
        e.status = status
        // Surface the server's error `code` too, so callers can branch on it
        // (e.g. RESHARER_NOT_CONNECTED) without parsing the message text.
        const c = (error as { code?: unknown }).code
        if (typeof c === 'string') e.code = c
        // #1152: a refusal about an ADDRESS names it (the invite-accept proof step shows which address a code goes to
        // before anything is sent).
        const em = (error as { email?: unknown }).email
        if (typeof em === 'string') e.email = em
        // Field-level validation failures (`{ path, message }[]`). A 422 whose
        // detail is dropped here becomes a toast that says "not valid" and
        // names no field — the caller cannot mark the offending input because
        // the only thing that knew which one it was got thrown away. Kept
        // generic rather than branding-specific: it is the shape every route
        // returning `issues` already uses.
        const iss = (error as { issues?: unknown }).issues
        if (Array.isArray(iss)) {
          const clean = iss.filter(
            (x): x is { path: string; message: string } =>
              !!x && typeof (x as { path?: unknown }).path === 'string'
              && typeof (x as { message?: unknown }).message === 'string',
          )
          if (clean.length) e.issues = clean
        }
        // Surface the org_email_required payload (a user with 2+ memberships in
        // one tenant must pick an identity) so the client can render an identity
        // picker — emails, their roles, and the tenant name — instead of a raw
        // error.
        const ae = (error as { availableEmails?: unknown }).availableEmails
        if (Array.isArray(ae) && ae.every(x => typeof x === 'string')) e.availableEmails = ae as string[]
        const ai = (error as { availableIdentities?: unknown }).availableIdentities
        if (Array.isArray(ai)) e.availableIdentities = ai as Array<{ email: string; role: string }>
        const tn = (error as { tenantName?: unknown }).tenantName
        if (typeof tn === 'string' || tn === null) e.tenantName = tn as string | null
        // 429 rate-limit hint (#341 Case D). The server puts `retryAfterSec` in
        // the body (the `Retry-After` header is not CORS-exposed). Surface it so
        // a listener can show "try again in N seconds".
        const ra = (error as { retryAfterSec?: unknown }).retryAfterSec
        if (typeof ra === 'number' && Number.isFinite(ra)) e.retryAfterSec = ra
        // NAME_CONFLICT (409, save-to-drive #662): the name that already
        // exists at the destination and the name a suffix-mode retry would
        // land under — so a caller (the Save-to-drive dialog) can render
        // "X already exists… will save as X(1)" straight off the caught
        // error, no second round-trip. Same "add a named optional field"
        // convention as `tenantName`/`retryAfterSec` above — this module
        // stays dumb about what NAME_CONFLICT MEANS, it just carries the
        // two strings through.
        const en = (error as { existingName?: unknown }).existingName
        if (typeof en === 'string') e.existingName = en
        const wsa = (error as { wouldSaveAs?: unknown }).wouldSaveAs
        if (typeof wsa === 'string') e.wouldSaveAs = wsa
        // The server's human sentence beside its code (`{ error, detail }`) — a caller
        // that shows the code alone prints "no_share_capability" at the user; a refusal
        // rendered in place (the share dialog's "can't serve this folder", #889) shows it.
        const dt = (error as { detail?: unknown }).detail
        if (typeof dt === 'string' && dt) e.detail = dt
        // #801 protected-branch refusal (409 PROTECTED_BRANCHES): whether the root is
        // an app/project (refused whole, never split), whether a split would leave the
        // root's own access setup behind (the mover lacks Manage on it), and whether it
        // would leave the MOVER unable to manage the new shell — the last of which can
        // only be learned from the act itself, never a preview. `deleteFile` is the one
        // `request()` caller these matter to today (`moveFile` uses its own manual fetch
        // and MoveRefusedError — same three fields, same names).
        const er = (error as { entityRoot?: unknown }).entityRoot
        if (typeof er === 'boolean') e.entityRoot = er
        const nm = (error as { needsManage?: unknown }).needsManage
        if (typeof nm === 'boolean') e.needsManage = nm
        const wla = (error as { wouldLoseAccess?: unknown }).wouldLoseAccess
        if (typeof wla === 'boolean') e.wouldLoseAccess = wla
        // #826 Bin restore whose folder is gone (409 RESTORE_NEEDS_REBUILD): the plan the
        // person confirms (the folders that come back, or the same-name folder it goes
        // into, plus the fingerprint the restore must match), whether they may pick
        // another folder instead, and the folder a refused rebuild names.
        const rp = (error as { plan?: unknown }).plan
        if (rp && typeof rp === 'object' && Array.isArray((rp as { recreate?: unknown }).recreate)
          && typeof (rp as { fingerprint?: unknown }).fingerprint === 'string') {
          e.restorePlan = rp as NonNullable<typeof e.restorePlan>
        }
        const cr = (error as { canRelocate?: unknown }).canRelocate
        if (typeof cr === 'boolean') e.canRelocate = cr
        // #826: RESTORE_NEWER_COPY — whether this person can restore the newer copy themselves.
        const crn = (error as { canRestoreNewer?: unknown }).canRestoreNewer
        if (typeof crn === 'boolean') e.canRestoreNewer = crn
        const fn = (error as { folderName?: unknown }).folderName
        if (typeof fn === 'string') e.folderName = fn
        // #1119 review round 5: a Bin rebuild refused 400 RESERVED_ROOT names the reserved folder it would recreate.
        const rn = (error as { reservedName?: unknown }).reservedName
        if (typeof rn === 'string') e.reservedName = rn
        // #360: org-connections `/initiate` refuses a whole multi-folder share when ONE folder
        // shared alongside (a chain target) can't go, and names it — so the share dialog can
        // say which folder its reason is about.
        const ffi = (error as { failedFolderId?: unknown }).failedFolderId
        if (typeof ffi === 'string' && ffi) e.failedFolderId = ffi
        // A db grid read refused for naming columns the table lacks (#899):
        // which ones, so the grid can forget exactly those.
        const uc = (error as { unknownColumns?: unknown }).unknownColumns
        if (Array.isArray(uc) && uc.every(x => typeof x === 'string')) e.unknownColumns = uc as string[]
        // #1430 (2): an extract refused part-way names what it already wrote (created ids, the files a Replace
        // versioned, the auto-created folder), so the drive can offer to undo exactly that.
        const pt = (error as { partial?: unknown }).partial as { fileIds?: unknown; topFileIds?: unknown; versioned?: unknown; destinationFolderId?: unknown } | undefined
        if (pt && typeof pt === 'object' && Array.isArray(pt.fileIds) && pt.fileIds.every(x => typeof x === 'string')) {
          e.partial = {
            fileIds: pt.fileIds as string[],
            ...(Array.isArray(pt.topFileIds) && pt.topFileIds.every(x => typeof x === 'string') ? { topFileIds: pt.topFileIds as string[] } : {}),
            versioned: Array.isArray(pt.versioned) ? pt.versioned as NonNullable<typeof e.partial>['versioned'] : [],
            destinationFolderId: typeof pt.destinationFolderId === 'string' ? pt.destinationFolderId : null,
          }
        }
        // E7 PR 2 slice 2: a SQLite version save's refusal says WHY it is unsaved (`superseded`: a newer drive version
        // replaces the change) — the db editor's background retry stops and tells the person that, never "in use".
        const vur = (error as { versionUnsavedReason?: unknown }).versionUnsavedReason
        if (typeof vur === 'string' && vur) e.versionUnsavedReason = vur
        // #359/#360: AMBIGUOUS_RECIPIENT — a bare-email share that reaches several of the
        // sender's own connections names them (a LIST: one person, any number of orgs), so
        // the share dialog can offer them to pick from and re-send with an explicit tenant.
        const cand = (error as { candidates?: unknown }).candidates
        if (Array.isArray(cand)) {
          const clean = cand.filter(
            (x): x is { tenantId: string; name: string; type: string } =>
              !!x && typeof (x as { tenantId?: unknown }).tenantId === 'string'
              && typeof (x as { name?: unknown }).name === 'string'
              && typeof (x as { type?: unknown }).type === 'string',
          )
          if (clean.length) e.candidates = clean
        }
        // Announce BEFORE throwing, so a listener that re-reads tenant state has
        // its request in flight while the caller is still rendering its own
        // failure. Ordering only; nothing here waits on the listeners.
        // #1316: a failure answered after its session ended tells no listener (a storage dialog, an access-denied screen)
        if (!sentAs.session?.aborted) reportApiFailure({
          status, code: e.code, path, retryAfterSec: e.retryAfterSec, body: error as Record<string, unknown>,
          ...(options.identity && !isCurrentIdentity(options.identity) ? { staleIdentity: true } : {}),
          ...(selfExplained && { selfExplained: true }),
        })
        throw e
      }

      // 503 = server temporarily overloaded — give user a clear message. A 503 that names a BUSY DATABASE
      // (`DB_BUSY` / `APPDB_BUSY`: an app db's write turn, or — H15 W6 — a SQLite lock that may be a
      // sandbox's) already carries the sentence that says why: keep it, it is not the server overloaded.
      if (status === 503) {
        const busyCode = (error as { code?: unknown }).code
        const serverMsg = (error as { error?: unknown }).error
        // Only a sentence written for people ("This database is …" — W6's in-use texts, the busy-write
        // texts): a busy code from a path that still sends an internal message gets the generic line.
        if ((busyCode === 'DB_BUSY' || busyCode === 'APPDB_BUSY') && typeof serverMsg === 'string' && serverMsg.startsWith('This database is')) fail(serverMsg)
        // #1090 / #1134 — a db being replaced, or a save of it still being written: the API's sentence says which (a
        // path that answers an internal token, e.g. `engine_db_replacing`, has no space and gets the generic line).
        if (busyCode === 'ENGINE_DB_REPLACING' && typeof serverMsg === 'string' && /\s/.test(serverMsg)) fail(serverMsg)
        fail('Server is busy, please try again in a moment')
      }

      // #1217: a proof-code refusal is a 429 that explains itself — "wait up to an hour" is not "try again in a
      // moment" — and the page that asked shows it, so its sentence is kept (and the global toast skips it).
      // The server says so with `selfExplained: true` (it knows its sentence; the client keeps no list of codes).
      if (selfExplained) {
        fail(error.error)
      }
      // 429 = rate limited — friendly message (the web layer shows an i18n toast
      // via onApiFailure; this is the thrown-error fallback text, like 503's).
      if (status === 429) {
        const raBody = (error as { retryAfterSec?: unknown }).retryAfterSec
        const secs = typeof raBody === 'number' && Number.isFinite(raBody) ? Math.ceil(raBody) : undefined
        fail(secs
          ? `Too many requests — please try again in ${secs} second${secs === 1 ? '' : 's'}`
          : 'Too many requests — please try again in a moment')
      }

      fail(error.error || 'Request failed')
    }

    // The timer bounds the wait for the RESPONSE, never the body's download (as before #1210: the body was read
    // after the `finally` had cleared it) — a large or slow body must not be cut at the stall timeout.
    if (timer) { clearTimeout(timer); timer = undefined }
    const body = await res.json()
    stampActingIdentity(body, res, headers)
    return body
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // #1316 review round 1: a request that names its own identity (`options.identity` — an undo's steps, D134) is work
  // that must FINISH in the session that started it — never dropped. Its caller shows or hides the outcome by its own
  // session signal (UndoProvider); the identity it is sent as is the pinned one, never the next session's.
  // (Review round 2: a step of a pinned batch sent through `batch.run` names it the same way.)
  const pin = options.identity ? null : takeStepPin()
  if (pin) return requestInSession<T>(path, { ...options, identity: pin })
  if (options.identity) return requestInSession<T>(path, options)
  // #1316: bound to the session current NOW — an answer after it ended becomes a SessionEndedError
  const session = currentSessionSignal()
  forgetInflightGetsWhenEnded(session)
  return dropIfSessionEnded(requestInSession<T>(path, options), session)
}

/** #1316: a session's in-flight GETs are never JOINED by the next session — its caller would wait on the old request
 *  and be handed its answer (its own drop check reads the NEW, live signal). Cleared when the session ends; the old
 *  promise's own `finally` deletes only itself. */
const watchedSessions = new WeakSet<AbortSignal>()
function forgetInflightGetsWhenEnded(session: AbortSignal | undefined): void {
  if (!session || session.aborted || watchedSessions.has(session)) return
  watchedSessions.add(session)
  session.addEventListener('abort', () => inflightGets.clear(), { once: true })
}

async function requestInSession<T>(path: string, options: RequestOptions): Promise<T> {
  const method = options.method || 'GET'
  const headers = {
    ...(options.identity ? identityHeaders(options.identity) : getDefaultHeaders({ path })),
    ...options.headers,
  }
  const sentAs = sentAsNow()

  // Only dedupe idempotent reads. Mutations must always pass through —
  // two seemingly-identical PUTs can have different server-side intent
  // (e.g. workspace state saves carry timestamps the server logs). They
  // also keep the 30s/no-retry path: a stalled POST may have committed.
  if (method !== 'GET') {
    try {
      return await doRequest<T>(path, options, headers, DEFAULT_REQUEST_TIMEOUT_MS, sentAs)
    } finally {
      // A MUTATION ENDS THE DEDUP WINDOW.
      //
      // Without this, a GET issued after a write can JOIN a flight that
      // started before it and resolve to a body fetched when the write did
      // not exist yet — a read-after-write failure with no error and no
      // second request in the network log, which makes it read as "the
      // client never tried".
      //
      // Found on stagetest: the keybook collections dialog issued a slow
      // mount `GET /collections` (507ms), Playwright typed and clicked Add
      // 54ms later, `POST /collections` returned 200, and the dialog's
      // `reload()` joined the still-in-flight mount read — rendering
      // "No collections yet." over a collection that had just been created.
      // Any create-then-reload in the app could hit this; the dialog was
      // only the fastest way to get inside the window.
      //
      // Cleared WHOLESALE and on COMPLETION, both deliberate. Wholesale
      // because a write's read-impact is not knowable from its path (a POST
      // to /collections also changes /entries listings); on completion so
      // the guarantee is the one callers actually rely on — a GET issued
      // after `await mutate()` starts a fresh flight. A GET issued DURING a
      // mutation is genuinely concurrent and is owed no ordering.
      //
      // The dedup itself is untouched for the case it exists for: concurrent
      // identical GETs with no write between them still share one fetch.
      inflightGets.clear()
    }
  }

  const key = dedupKey(path, headers)
  const existing = inflightGets.get(key)
  // Concurrent identical GETs share one in-flight promise. A late joiner thus
  // inherits the original request's remaining stall-timeout window rather than
  // a fresh one — if it joins mid-stall it gets a degraded budget. It still
  // rides the single shared retry (it does NOT get zero retries), so this is a
  // bounded suboptimality, not a correctness issue (R5). Acceptable: the dedup
  // saves a duplicate round-trip through the same stalling edge.
  if (existing) return existing as Promise<T>

  const promise = getWithStallRetry<T>(path, options, headers, sentAs).finally(() => {
    if (inflightGets.get(key) === promise) inflightGets.delete(key)
  })
  inflightGets.set(key, promise)
  return promise as Promise<T>
}

/**
 * GET with one stall-retry. A GET whose response is lost/badly-delayed by a
 * Cloudflare edge stall trips GET_STALL_TIMEOUT_MS (our own abort, surfaced
 * as a TimeoutError); re-issuing gets a fresh connection and the origin —
 * which answered the first attempt in <1s — responds immediately.
 *
 * Scope is deliberately narrow:
 *  - GET only (caller filters): never retry a mutation.
 *  - Retry ONLY our timeout abort. A real HTTP error carries `.status`; a
 *    genuine network failure is a TypeError; a caller-initiated abort is the
 *    caller's intent — none of those are a stall, so they propagate as-is.
 *  - Caller-supplied signal opts out of both the tight timeout and the retry
 *    (it owns the request lifecycle), same contract as the default timeout.
 *  - One retry only: bounded, so a persistently-down origin fails in ~16s
 *    instead of amplifying load.
 */
async function getWithStallRetry<T>(
  path: string,
  options: RequestOptions,
  headers: Record<string, string>,
  sentAs: SentAs,
): Promise<T> {
  if (options.signal) {
    return doRequest<T>(path, options, headers, DEFAULT_REQUEST_TIMEOUT_MS, sentAs)
  }
  try {
    return await doRequest<T>(path, options, headers, GET_STALL_TIMEOUT_MS, sentAs)
  } catch (err) {
    const isStall = err instanceof DOMException && err.name === 'TimeoutError'
    if (!isStall) throw err
    // #1316: the retry would be sent as the NEXT session — unless it names its own (pinned) identity: then it is sent
    // as that one again, and it must finish
    if (!options.identity && sentAs.session?.aborted) throw err
    return doRequest<T>(path, options, headers, GET_STALL_TIMEOUT_MS, sentAs)
  }
}

/**
 * Make a raw fetch request with default headers (for blob/stream responses)
 */
export function fetchRaw(path: string, options: RequestInit & { identity?: RequestIdentity } = {}): Promise<Response> {
  // #1316 review round 2: a request naming its own identity (a pinned batch's step, `pinnedBatch`) finishes, as request()
  const pin = options.identity ? null : takeStepPin()
  if (pin) return fetchRawInSession(path, { ...options, identity: pin })
  if (options.identity) return fetchRawInSession(path, options)
  // #1316: bound to the session current NOW, like request() — its body reads too (codex review)
  const session = currentSessionSignal()
  return dropIfSessionEnded(fetchRawInSession(path, options), session).then((res) => bindBodyToSession(res, session))
}

async function fetchRawInSession(path: string, options: RequestInit & { identity?: RequestIdentity }): Promise<Response> {
  const { identity, ...init } = options
  const headers = {
    ...(identity ? identityHeaders(identity) : getDefaultHeaders({ path })),
    ...(init.headers as Record<string, string> | undefined),
  }
  const sentAccount = accountGetter?.()
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    credentials: 'include',
  })
  noteActingIdentity(res, headers, sentAccount)   // D134: every first-party answer feeds the identity echo
  return res
}
